/**
 * §P.2 · El horario semanal de un grupo.
 *
 * Cuatro reglas, todas de `BFF_BO.md` §7.5, y todas con una consecuencia visible:
 *
 * **`weekday` es ISO: 1 es lunes.** No la convención de JavaScript. Los días salen
 * de `DIAS`, indexado por el valor ISO.
 *
 * **Se manda la semana completa.** Es un `PUT`: lo que no se manda desaparece. Se
 * edita como un bloque porque así se edita en la pantalla.
 *
 * **Las horas son locales del club.** No se convierten aquí: lo hace PostgreSQL al
 * generar las sesiones, con la zona del club, y lo hace bien incluso el fin de
 * semana del cambio de hora — que es cuando convertir en JavaScript desplazaría
 * todas las clases del club.
 *
 * **Cambiar el horario no toca las clases ya generadas**, y eso hay que decirlo en
 * la pantalla: si no, alguien cambia el horario en noviembre esperando que se
 * arreglen las clases de octubre.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import {
  diasQueSePisan,
  franjaValida,
  DIAS,
  DIAS_ISO,
  MAXIMO_DE_FRANJAS,
  type Franja,
} from '@/club/grupos/tipos';

const nueva = (): Franja => ({
  weekday: 1,
  starts_at: '17:30',
  ends_at: '18:30',
});

/** Agrupa por día ISO, para leerlo como una semana y no como una lista. */
const porDia = (franjas: readonly Franja[]) =>
  DIAS_ISO.map((dia) => ({
    dia,
    franjas: franjas
      .filter((f) => f.weekday === dia)
      .slice()
      .sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
  })).filter((d) => d.franjas.length > 0);

export function HorarioSemanal({
  club,
  grupoId,
  puedeEditar,
}: {
  club: string;
  grupoId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [borrador, setBorrador] = useState<readonly Franja[] | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const horario = useQuery({
    queryKey: ['horario', club, grupoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .groups({ groupId: grupoId })
        .schedules.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Franja[];
    },
  });

  const guardar = useMutation({
    mutationFn: async (franjas: readonly Franja[]) => {
      const r = await api.v1
        .clubs({ club })
        .groups({ groupId: grupoId })
        .schedules.put({
          // La semana completa y solo las válidas: una fila a medio escribir no
          // se manda, pero tampoco borra el resto.
          schedules: franjas.filter(franjaValida).map((f) => ({
            weekday: f.weekday,
            starts_at: f.starts_at,
            ends_at: f.ends_at,
          })),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      setBorrador(null);
      void cache.invalidateQueries({ queryKey: ['horario', club, grupoId] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (horario.isPending)
    return <Cargando filas={2} etiqueta="Cargando el horario" />;

  if (horario.isError)
    return (
      <PanelError
        problema={aProblema(horario.error)}
        reintentar={() => void horario.refetch()}
      />
    );

  // ── Edición: la semana entera ────────────────────────────────────────────
  if (borrador) {
    const conflictos = diasQueSePisan(borrador);
    const validas = borrador.filter(franjaValida).length;

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate(borrador);
        }}
      >
        <p className="tenue">
          Las horas son <strong>las del club</strong>, tal cual. Se edita la
          semana entera: lo que quites, desaparece.
        </p>

        {conflictos.length > 0 && (
          // El día concreto, no un aviso genérico: el servidor responde
          // `422 overlapping_schedules` y aquí se dice cuál es.
          <div className="aviso" role="alert">
            <strong>
              Hay clases que se pisan{' '}
              {conflictos.length === 1
                ? `el ${DIAS[conflictos[0]!]!.toLowerCase()}`
                : `estos días: ${conflictos.map((d) => DIAS[d]!.toLowerCase()).join(', ')}`}
              .
            </strong>{' '}
            Sería la misma gente en dos sitios a la vez, así que no se puede
            guardar. Dos clases seguidas —una acaba a las 18:30 y la otra
            empieza a las 18:30— sí valen.
          </div>
        )}

        <ul className="franjas">
          {borrador.map((f, i) => {
            const enConflicto = conflictos.includes(f.weekday);
            const mal = !franjaValida(f) && (f.starts_at || f.ends_at);

            return (
              <li
                key={i}
                className={enConflicto || mal ? 'franja-mal' : undefined}
              >
                <select
                  aria-label={`Día de la franja ${i + 1}`}
                  value={f.weekday}
                  onChange={(e) =>
                    setBorrador(
                      borrador.map((x, j) =>
                        j === i ? { ...x, weekday: Number(e.target.value) } : x
                      )
                    )
                  }
                >
                  {DIAS_ISO.map((d) => (
                    <option key={d} value={d}>
                      {DIAS[d]}
                    </option>
                  ))}
                </select>

                <input
                  type="time"
                  aria-label={`Hora de inicio de la franja ${i + 1}`}
                  value={f.starts_at}
                  onChange={(e) =>
                    setBorrador(
                      borrador.map((x, j) =>
                        j === i ? { ...x, starts_at: e.target.value } : x
                      )
                    )
                  }
                />

                <span className="tenue">a</span>

                <input
                  type="time"
                  aria-label={`Hora de fin de la franja ${i + 1}`}
                  value={f.ends_at}
                  onChange={(e) =>
                    setBorrador(
                      borrador.map((x, j) =>
                        j === i ? { ...x, ends_at: e.target.value } : x
                      )
                    )
                  }
                />

                <button
                  type="button"
                  className="btn-plano"
                  aria-label={`Quitar la franja ${i + 1}`}
                  onClick={() =>
                    setBorrador(borrador.filter((_, j) => j !== i))
                  }
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>

        {borrador.length < MAXIMO_DE_FRANJAS && (
          <button
            type="button"
            className="btn-plano"
            onClick={() => setBorrador([...borrador, nueva()])}
          >
            Añadir una clase
          </button>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button
            type="submit"
            disabled={guardar.isPending || conflictos.length > 0}
          >
            {guardar.isPending
              ? 'Guardando…'
              : validas === 0
                ? 'Guardar sin horario'
                : `Guardar ${validas} clase${validas === 1 ? '' : 's'} a la semana`}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={guardar.isPending}
            onClick={() => {
              setBorrador(null);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>

        {/* Lo que la gente da por hecho y no es verdad. */}
        <p className="tenue">
          Los cambios se aplicarán{' '}
          <strong>al generar el próximo periodo</strong>: las clases que ya
          están en el calendario no se mueven.
        </p>
      </form>
    );
  }

  // ── Lectura: la semana ───────────────────────────────────────────────────
  if (horario.data.length === 0) {
    return (
      <Vacio
        titulo="Este grupo no tiene horario"
        explicacion="Sin horario no se generan clases, así que no hay dónde pasar lista. Di qué días y a qué hora se da."
        {...(puedeEditar
          ? {
              accion: (
                <button type="button" onClick={() => setBorrador([nueva()])}>
                  Poner el horario
                </button>
              ),
            }
          : {})}
      />
    );
  }

  return (
    <>
      <ul className="semana">
        {porDia(horario.data).map(({ dia, franjas }) => (
          <li key={dia}>
            <strong>{DIAS[dia]}</strong>
            <span>
              {franjas.map((f) => `${f.starts_at}–${f.ends_at}`).join(' · ')}
            </span>
          </li>
        ))}
      </ul>

      {puedeEditar && (
        <div className="acciones">
          <button
            type="button"
            className="btn-plano"
            onClick={() => setBorrador(horario.data.map((f) => ({ ...f })))}
          >
            Editar el horario
          </button>
        </div>
      )}
    </>
  );
}
